export const resources = {
  en: {
    translation: {
      nav: {
        howItWorks: "How It Works",
        products: "Products",
        contracts: "Certificates",
        pricing: "Pricing",
        contact: "Contact Us",
        support: "Support"
      },
      hero: {
        status: "Live Network Status: Operational",
        heading: {
          instant: "VERIFY.",
          secure: "CERTIFY.",
          anonymous: "DELIVER."
        },
        subtitle: "Enabling technology certificates, one transaction at a time.",
        trustIndicators: {
          security: "Enterprise Security",
          global: "Global Coverage",
          instant: "Real-time Delivery"
        },
        cta: "Get in Touch",
        pricing: "View Pricing"
      },
      poweredBySODL: {
        label: "INFRASTRUCTURE",
        titlePrefix: "Powered by",
        titleMain: "CORNETTO PLATFORM",
        subtitle: "Next-generation global networking architecture designed for international scale. This framework is managed by Cornetto Platform, a Cyprus-based entity that ensures alignment with stringent European security standards and operational mandates.",
        processor: "Cornetto PROCESSOR v2.0",
        online: "ONLINE",
        streams: {
          payment: "TRANSMIT",
          verify: "VALIDATE",
          mint: "ENCRYPT",
          settle: "DEPLOY"
        },
        stats: {
          throughput: "Potential Throughput",
          latency: "Latency",
          uptime: "Uptime"
        },
        features: {
          decentralized: {
            title: "Decentralized Network",
            description: "800+ validators worldwide"
          },
          secure: {
            title: "Highly Secure",
            description: "End-to-end encryption"
          },
          fast: {
            title: "Lightning Fast",
            description: "Sub-second processing"
          }
        },
        allSystemsOperational: "All Systems Operational"
      },
      howItWorks: {
        badge: "The Securing Process",
        title: "How It",
        titleHighlight: "Works",
        subtitle: "From a simple checkout to a verified hand-off in three seamless steps. Your customers use the tools they already know—no technical expertise required. The Cornetto Platform creates an automated technology agreement, ensuring every transaction is protected.",
        steps: {
          payment: {
            title: "Secure Entry",
            description: "Users connect via Cornetto Pro. Our universal entry point manages access requests with multi-layered technology security built-in.",
            highlights: ["Visa & Mastercard", "Apple Pay", "Google Pay", "3DS Security"]
          },
          guaranteeService: {
            title: "Cornetto Pro Securely Guarantees Service",
            description: "By securely verifying service, Cornetto Pro extends its multi-layered technology security from the point of entry through to the final user interaction.",
            highlights: ["On-chain Confirmation", "Verified Delivery"]
          },
          merchantDelivery: {
            title: "Service Delivered",
            description: "The Cornetto Pro Platform guarantees the service has been provided.",
            highlights: ["On-chain Confirmation", "Verified Delivery"]
          }
        },
        bottomStats: {
          speed: { value: "<4 Seconds", label: "End-to-End" },
          security: { value: "PCI Compliant", label: "Payment Security" },
        }
      },
      pricing: {
        badge: "Enterprise Pricing",
        title: "Your Technology Certificate",
        titleHighlight: "Your Price",
        subtitle: "We work directly with industries to craft pricing that reflects how we verify, certify and deliver for the new age. Security is pivotal in a fast-moving world. Our platform provides a technology tracking code for electronic goods.",
        volumeNote: "Our pricing is based on volume, use case, and integration scope. Reach out and we'll put together a custom proposal within 24 hours.",
        cta: "Request Pricing Quote",
        bottomNote: "All plans include 99.9% uptime SLA, and dedicated onboarding support.",
        features: {
          volumeBased: "Volume-Based Pricing",
          global: "Verify",
          escrow: "Certify",
          instant: "Deliver",
          dedicated: "Dedicated Account Manager",
          integration: "Custom API Integration"
        }
      },
      products: {
        badge: "Core Products",
        title: "One Platform.",
        titleHighlight: "Three Pillars.",
        subtitle: "Verify, Certify, Deliver are the three mandatory pillars required for a technology transaction to guarantee delivery of service. This is what the Cornetto platform enables.",
        pillars: {
          pay: {
            title: "Cornetto Pro Verify",
            subtitle: "Verification Engine",
            description: "An API request is initiated by the end user to access the Cornetto service.",
            features: ["SSL", "HTTPS"],
            statValue: "99.9%",
            statLabel: "Uptime"
          },
          mint: {
            title: "Cornetto Pro Certify",
            subtitle: "Certification Engine",
            description: "The Cornetto platform securely stamps the verification request and authenticates it via our technological solution.",
            features: ["SHA256"],
            statValue: "<1s",
            statLabel: "Time Taken"
          },
          settlements: {
            title: "Cornetto Pro Deliver",
            subtitle: "Delivery Engine",
            description: "The generated certificate is then combined with an API call and activated through the Cornetto platform.",
            features: ["OAuth", "TLS"],
            statValue: "24/7",
            statLabel: "Settlement"
          }
        }
      },
      smartContracts: {
        badge: "Technology Certificates",
        title: "Verification through",
        titleHighlight: "Certification",
        description: "We provide the technical services of verifying transactions and certifying their veracity. No delays just robust integral proof of delivery.",
        descriptionTagline: "We provide tracking codes for the digital age.",
        images: {
          first: {
            caption: "A tracking number offers a reliable proof of delivery solution for physical goods. Once a novel concept, it has now become a worldwide standard."
          },
          second: {
            caption: "With the growth of the global economy our system provides seamless support to certify the veracity of transactions between participating parties. Our certification solution guarantees proof of delivery for both parties, ensuring that what was promised corresponds exactly to what was delivered."
          }
        },
        benefits: {
          instant: {
            title: "Instant Settlement",
            description: "Sub-30 second end-to-end processing."
          },
          compliance: {
            title: "Enterprise Security",
            description: "PCI DSS compliant with multi-layer authentication."
          },
          dispute: {
            title: "Blockchain Native",
            description: "Solana integration for instant token* minting."
          },
          regulatory: {
            title: "Deterministic Finality",
            description: "Instant, irreversible settlement with immediate cryptographic verification."
          },
          multiCurrency: {
            title: "Multi-Currency Support",
            description: "Execute transactions in any supported currency pair."
          }
        }
      },
      contact: {
        badge: "Contact Us",
        title: "Get in",
        titleHighlight: "Touch",
        description: "Have questions about Cornetto Pro? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
        form: {
          firstName: "First Name",
          lastName: "Last Name",
          emailAddress: "Email Address",
          subject: "Subject",
          selectTopic: "Select a topic",
          topics: {
            general: "General Inquiry",
            api: "API Integration",
            enterprise: "Enterprise Solutions",
            partnership: "Partnership",
            support: "Technical Support",
            pricing: "Pricing"
          },
          message: "Message",
          messagePlaceholder: "Tell us how we can help you...",
          submit: "Send Message",
          sending: "Sending…"
        },
        success: {
          title: "Message Sent!",
          body: "Thanks for reaching out. We'll get back to you shortly.",
          another: "Send another message"
        }
      },
      footer: {
        description: "",
        columns: {
          product: "Service",
          legal: "Legal",
          support: "Support"
        },
        links: {
          smartContracts: "Certificates",
          features: "Products",
          support: "Support",
          contactUs: "Contact Us",
          privacyPolicy: "Privacy Policy",
          terms: "Terms & Conditions",
          cookies: "Cookies Policy",
        },
        rights: "Registered in Cyprus, EU. All rights reserved."
      }
    }
  }
};
